import {
  addMonths,
  addWeeks,
  getWeeksInMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns"
import addDays from "date-fns/addDays"
import { indexes } from "../../../utils/array"
import { CalendarDate, CalendarWeekDates } from "./types"

const FIRST_DAY_OF_WEEK = 1

const fromDate = (d: Date): CalendarDate => ({
  day: d.getDate(),
  year: d.getFullYear(),
  month: d.getMonth() + 1,
})

const toDate = (d: CalendarDate): Date => new Date(d.year, d.month - 1, d.day)

const getFirstDateOfWeek = (date: Date, firstDayOfWeek: number) =>
  startOfWeek(date, { weekStartsOn: firstDayOfWeek as any })

export const formatIsoDate = (date: CalendarDate) =>
  `${date.year}-${date.month
    .toString()
    .padStart(2, "0")}-${date.day.toString().padStart(2, "0")}`

export const todayDate = () => {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export const addCalendarMonths = (date: CalendarDate, value: number) =>
  fromDate(addMonths(toDate(date), value))

export const addCalendarDays = (date: CalendarDate, value: number) =>
  fromDate(addDays(toDate(date), value))

export const todayCalendarDate = (): CalendarDate => {
  const d = new Date()
  return {
    day: d.getDate(),
    month: d.getMonth() + 1,
    year: d.getFullYear(),
  }
}

const buildWeek = (
  startWeekDate: CalendarDate,
  monthStartDate: CalendarDate
): CalendarWeekDates => {
  const week: CalendarWeekDates = []
  for (const dayIndex of [0, 1, 2, 3, 4, 5, 6]) {
    const d = addCalendarDays(startWeekDate, dayIndex)
    if (d.month === monthStartDate.month) {
      week.push(d)
    } else {
      week.push(undefined)
    }
  }
  return week
}

export const getMonthWeeks = (refDate: CalendarDate): CalendarWeekDates[] => {
  const monthDate = toDate(refDate)
  const monthStartDate = startOfMonth(monthDate)
  const totWeeks = getWeeksInMonth(monthStartDate, {
    weekStartsOn: FIRST_DAY_OF_WEEK,
  })
  const firstWeekDate = getFirstDateOfWeek(monthStartDate, FIRST_DAY_OF_WEEK)

  return indexes(0, totWeeks).map((x) =>
    buildWeek(fromDate(addWeeks(firstWeekDate, x)), fromDate(monthStartDate))
  )
}

export const getCalendarDayNumber = (d: CalendarDate) => {
  return toDate(d).getDay()
}

export const getCalendarDayName = (d: CalendarDate) => {
  switch (toDate(d).getDay()) {
    case 0:
      return "Domenica"
    case 1:
      return "Lunedì"
    case 2:
      return "Martedì"
    case 3:
      return "Mercoledì"
    case 4:
      return "Giovedì"
    case 5:
      return "Venerdì"
    case 6:
      return "Sabato"
  }
}

export const getMonthName = (d: CalendarDate) => {
  switch (d.month) {
    case 1:
      return "Gennaio"
    case 2:
      return "Febbraio"
    case 3:
      return "Marzo"
    case 4:
      return "Aprile"
    case 5:
      return "Maggio"
    case 6:
      return "Giugno"
    case 7:
      return "Luglio"
    case 8:
      return "Agosto"
    case 9:
      return "Settembre"
    case 10:
      return "Ottobre"
    case 11:
      return "Novembre"
    case 12:
      return "Dicembre"
  }
}

export const dateEquals = (val1: CalendarDate, val2: CalendarDate) =>
  val1 &&
  val2 &&
  val1.day === val2.day &&
  val1.month === val2.month &&
  val1.year === val2.year
